// Hoisted regex patterns (js-hoist-regexp: avoid recreating in render loops)
const HAS_DIGIT = /\d/;
const HAS_UPPERCASE = /[A-Z]/;
const HAS_LOWERCASE = /[a-z]/;
const HAS_SPECIAL = /[^a-zA-Z0-9]/;

interface PasswordRequirement {
  readonly label: string;
  readonly test: (password: string) => boolean;
}

export const PASSWORD_REQUIREMENTS: readonly PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { label: 'Contains a number', test: (pw) => HAS_DIGIT.test(pw) },
  { label: 'Contains uppercase letter', test: (pw) => HAS_UPPERCASE.test(pw) },
  { label: 'Contains lowercase letter', test: (pw) => HAS_LOWERCASE.test(pw) },
];

export interface PasswordRequirementResult {
  label: string;
  met: boolean;
}

export function getPasswordRequirements(password: string): PasswordRequirementResult[] {
  return PASSWORD_REQUIREMENTS.map((req) => ({
    label: req.label,
    met: req.test(password),
  }));
}

export function isPasswordValid(password: string): boolean {
  return PASSWORD_REQUIREMENTS.every((req) => req.test(password));
}

export function getPasswordStrength(password: string): {
  score: number;
  label: string;
  color: string;
} {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (HAS_LOWERCASE.test(password) && HAS_UPPERCASE.test(password)) score++;
  if (HAS_DIGIT.test(password)) score++;
  if (HAS_SPECIAL.test(password)) score++;

  if (score <= 1) return { score, label: 'Weak', color: 'bg-red-500' };
  if (score <= 2) return { score, label: 'Fair', color: 'bg-orange-500' };
  if (score <= 3) return { score, label: 'Good', color: 'bg-yellow-500' };
  if (score <= 4) return { score, label: 'Strong', color: 'bg-green-500' };
  return { score, label: 'Very Strong', color: 'bg-emerald-500' };
}
